(() => {
  const config = window.trolliedPartyBooking;
  if (!config) return;

  const MIN = Number(config.min) || 10;
  const MAX = Number(config.max) || 40;
  const MIX_MESSAGE = config.mixErrorMessage || 'You can only book one TROLLIED package per order. Please remove the other package from your cart first, or continue with your current selection.';
  const PARTY_HANDLES = new Set((config.handles || (config.handle ? [config.handle] : [])).map((handle) => String(handle)));
  const PARTY_VARIANT_IDS = new Set((config.variantIds || []).map((id) => String(id)));
  const VARIANT_MAP = config.variantMap || {};
  const originalFetch = window.fetch.bind(window);

  const isPartyVariant = (id) => PARTY_VARIANT_IDS.has(String(id)) || Boolean(VARIANT_MAP[String(id)]);
  const isPartyHandle = (handle) => PARTY_HANDLES.has(String(handle));
  const isPartyCartItem = (item) => Boolean(item && (isPartyVariant(item.variant_id) || isPartyHandle(item.handle)));

  const getVariantMeta = (variantId) => VARIANT_MAP[String(variantId)] || null;

  const wouldMixPartyTiers = (cart, variantId) => {
    if (!isPartyVariant(variantId)) return false;

    const meta = getVariantMeta(variantId);
    if (!meta) return false;

    const partyItems = (cart.items || []).filter((item) => isPartyCartItem(item));
    if (!partyItems.length) return false;

    return partyItems.some((item) => String(item.product_id) !== String(meta.productId));
  };

  const showMixError = (message = MIX_MESSAGE) => {
    // Prefer an existing on-page notice — don't stack banners/errors.
    const existingNotice = document.querySelector('[data-party-mix-notice]');
    if (existingNotice) {
      existingNotice.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const qtyError = document.querySelector('[data-qty-error-message]');
    if (qtyError) {
      const label = qtyError.querySelector('span') || qtyError;
      label.textContent = message;
      qtyError.classList.remove('hidden');
      qtyError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    let banner = document.querySelector('[data-party-mix-banner]');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'party-mix-banner errors';
      banner.setAttribute('data-party-mix-banner', '');
      banner.setAttribute('role', 'alert');
      document.body.prepend(banner);
    }
    banner.innerHTML = `<p>${message}</p>`;
    banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const mixBlockedResponse = (message = MIX_MESSAGE) => {
    showMixError(message);
    return new Response(JSON.stringify({
      status: 422,
      message,
      description: message
    }), {
      status: 422,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    });
  };

  const clampQuantity = (value, { allowZero = false } = {}) => {
    const quantity = parseInt(value, 10);
    if (allowZero && quantity === 0) return 0;
    if (Number.isNaN(quantity)) return MIN;
    return Math.min(MAX, Math.max(MIN, quantity));
  };

  const getUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input === 'object' && 'url' in input) return input.url;
    return '';
  };

  const isCartEndpoint = (url, type) => {
    const path = String(url || '').split('?')[0];
    return path.includes(`/cart/${type}`);
  };

  const cloneInit = (init = {}) => {
    const next = { ...init };
    if (init.headers) {
      next.headers = init.headers instanceof Headers ? new Headers(init.headers) : { ...init.headers };
    }
    return next;
  };

  const getHeader = (headers, name) => {
    if (!headers) return '';
    if (headers instanceof Headers) return headers.get(name) || '';
    const match = Object.keys(headers).find((key) => key.toLowerCase() === name.toLowerCase());
    return match ? headers[match] : '';
  };

  const parseBody = async (init = {}) => {
    const body = init.body;
    if (!body) return { type: 'empty', data: null };

    if (typeof body === 'string') {
      const contentType = getHeader(init.headers, 'Content-Type');
      if (contentType.includes('application/json') || body.trim().startsWith('{') || body.trim().startsWith('[')) {
        try {
          return { type: 'json', data: JSON.parse(body) };
        } catch (error) {
          return { type: 'string', data: body };
        }
      }

      const params = new URLSearchParams(body);
      return { type: 'params', data: params };
    }

    if (body instanceof URLSearchParams) {
      return { type: 'params', data: new URLSearchParams(body.toString()) };
    }

    if (body instanceof FormData) {
      const params = new URLSearchParams();
      body.forEach((value, key) => {
        if (typeof value === 'string') params.append(key, value);
      });
      return { type: 'form', data: body, params };
    }

    return { type: 'unknown', data: body };
  };

  const getCart = async () => {
    const response = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  };

  const getExistingQuantity = (cart, variantId) => {
    const item = cart.items.find((cartItem) => String(cartItem.variant_id) === String(variantId));
    return item ? item.quantity : 0;
  };

  const getExistingProductQuantity = (cart, productId) => {
    return (cart.items || [])
      .filter((item) => String(item.product_id) === String(productId))
      .reduce((total, item) => total + item.quantity, 0);
  };

  const clampAddQuantity = (variantId, requested, existing) => {
    let quantity = clampQuantity(requested);
    const remaining = Math.max(0, MAX - existing);
    quantity = Math.min(quantity, remaining);

    if (existing === 0 && quantity > 0 && quantity < MIN) {
      quantity = Math.min(MIN, remaining);
    }

    return quantity;
  };

  const applyAddConstraints = async (payload) => {
    const cart = await getCart();

    const prepareItem = (item) => {
      if (!item || !isPartyVariant(item.id)) return { item, blocked: false };
      if (wouldMixPartyTiers(cart, item.id)) {
        return { item, blocked: true };
      }

      const meta = getVariantMeta(item.id);
      const existing = meta
        ? getExistingProductQuantity(cart, meta.productId)
        : getExistingQuantity(cart, item.id);

      return {
        item: { ...item, quantity: clampAddQuantity(item.id, item.quantity || 1, existing) },
        blocked: false
      };
    };

    if (Array.isArray(payload.items)) {
      const nextItems = [];
      for (const item of payload.items) {
        const result = prepareItem(item);
        if (result.blocked) return { blocked: true };
        nextItems.push(result.item);
      }
      payload.items = nextItems;
      return { payload, blocked: false };
    }

    if (payload.id && isPartyVariant(payload.id)) {
      if (wouldMixPartyTiers(cart, payload.id)) return { blocked: true };
      const meta = getVariantMeta(payload.id);
      const existing = meta
        ? getExistingProductQuantity(cart, meta.productId)
        : getExistingQuantity(cart, payload.id);
      payload.quantity = clampAddQuantity(payload.id, payload.quantity || 1, existing);
    }

    return { payload, blocked: false };
  };

  const applyParamsAddConstraints = async (params) => {
    const id = params.get('id');
    if (!isPartyVariant(id)) return { params, blocked: false };

    const cart = await getCart();
    if (wouldMixPartyTiers(cart, id)) return { blocked: true };

    const meta = getVariantMeta(id);
    const existing = meta
      ? getExistingProductQuantity(cart, meta.productId)
      : getExistingQuantity(cart, id);
    params.set('quantity', String(clampAddQuantity(id, params.get('quantity') || 1, existing)));
    return { params, blocked: false };
  };

  const applyChangeConstraints = async (payload) => {
    if (payload.quantity === 0 || payload.quantity === '0') return payload;

    if (payload.id && isPartyVariant(payload.id)) {
      payload.quantity = clampQuantity(payload.quantity);
      return payload;
    }

    if (payload.line) {
      const cart = await getCart();
      const item = cart.items[Number(payload.line) - 1];
      if (isPartyCartItem(item)) {
        payload.quantity = clampQuantity(payload.quantity);
      }
    }

    if (payload.updates && typeof payload.updates === 'object') {
      const cart = await getCart();
      Object.keys(payload.updates).forEach((key) => {
        const item = cart.items.find((cartItem) => String(cartItem.key) === String(key) || String(cartItem.variant_id) === String(key) || String(cartItem.id) === String(key));
        if (isPartyCartItem(item)) {
          payload.updates[key] = clampQuantity(payload.updates[key], { allowZero: true });
        }
      });
    }

    return payload;
  };

  const rebuildInitBody = (init, parsed, payload) => {
    const next = cloneInit(init);

    if (parsed.type === 'json') {
      next.body = JSON.stringify(payload);
      return next;
    }

    if (parsed.type === 'params') {
      next.body = payload.toString();
      return next;
    }

    if (parsed.type === 'form') {
      if (payload instanceof URLSearchParams) {
        parsed.data.set('quantity', payload.get('quantity'));
      }
      next.body = parsed.data;
      return next;
    }

    return next;
  };

  window.fetch = async (input, init = {}) => {
    const url = getUrl(input);

    try {
      if (isCartEndpoint(url, 'add')) {
        const parsed = await parseBody(init);
        if (parsed.type === 'json') {
          const result = await applyAddConstraints(parsed.data);
          if (result.blocked) return mixBlockedResponse();
          init = rebuildInitBody(init, parsed, result.payload);
        } else if (parsed.type === 'params' || parsed.type === 'form') {
          const result = await applyParamsAddConstraints(parsed.params || parsed.data);
          if (result.blocked) return mixBlockedResponse();
          init = rebuildInitBody(init, parsed, result.params);
        }
      }

      if (isCartEndpoint(url, 'change') || isCartEndpoint(url, 'update')) {
        const parsed = await parseBody(init);
        if (parsed.type === 'json') {
          const payload = await applyChangeConstraints(parsed.data);
          init = rebuildInitBody(init, parsed, payload);
        }
      }
    } catch (error) {
      console.error('Unable to enforce Christmas party quantity:', error);
    }

    return originalFetch(input, init);
  };

  const clampPartyFields = () => {
    document.querySelectorAll('[data-party-booking] input[name="quantity"], [data-party-booking] input[name="quantity-js"], [data-party-booking] [data-cart-quantity-input], [data-party-booking] .js-qty__num').forEach((input) => {
      const allowZero = input.getAttribute('min') === '0';
      const nextValue = clampQuantity(input.value, { allowZero });
      if (String(input.value) !== String(nextValue)) {
        input.value = nextValue;
      }
    });
  };

  const enforceExistingCart = async () => {
    try {
      const cart = await getCart();
      let changed = false;

      for (let index = 0; index < cart.items.length; index += 1) {
        const item = cart.items[index];
        if (!isPartyCartItem(item)) continue;

        const nextQuantity = clampQuantity(item.quantity);
        if (nextQuantity === item.quantity) continue;

        const response = await originalFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: index + 1, quantity: nextQuantity })
        });
        const result = await response.json();
        if (!result.errors) changed = true;
      }

      if (changed) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Unable to correct Christmas party cart quantity:', error);
    }
  };

  const bindCartPageQuantity = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-party-booking] .js-qty__adjust');
      if (!button) return;

      if (button.closest('quantity-input')) return;

      const input = button.closest('.js-qty')?.querySelector('input');
      if (!input) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const adjustment = button.classList.contains('js-qty__adjust--minus') ? -1 : 1;
      input.value = clampQuantity(parseInt(input.value, 10) + adjustment);
    }, true);
  };

  const bindCheckoutGuard = () => {
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[name="checkout"], #CartDrawer-Checkout, .shopify-payment-button')) return;
      clampPartyFields();

      if (document.querySelector('[data-party-mix-notice], [data-party-checkout-blocked]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showMixError();
      }
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      clampPartyFields();

      form.querySelectorAll('[data-party-booking] input[name="updates[]"]').forEach((input) => {
        input.value = clampQuantity(input.value, { allowZero: true });
      });

      if (form.querySelector('[name="checkout"]') && document.querySelector('[data-party-checkout-blocked]')) {
        event.preventDefault();
        showMixError();
      }
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    clampPartyFields();
    bindCartPageQuantity();
    bindCheckoutGuard();
    enforceExistingCart();
  });
})();
